import { tryOnUnmounted } from '@vueuse/core';
import snakeCase from 'lodash/snakeCase';
import { computed, nextTick, readonly, ref } from 'vue';
import { isNavigationFailure, useRoute, useRouter } from 'vue-router';

import { useAuthStore } from '@/stores/auth';
import { useTrackingStore } from '@/stores/tracking';
import { useUtmStore } from '@/stores/utm';

const copyQueryParams = ref(false);
const includeQueryParams = ref([]);
const excludeQueryParams = ref([]);
const isBrandSwitching = ref(false);

const includeQueryParamsToCheck = computed(() =>
  includeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

const excludeQueryParamsToCheck = computed(() =>
  excludeQueryParams.value.flatMap((param) => [param, snakeCase(param)]),
);

export function useBrandSwitcher() {
  const route = useRoute();
  const router = useRouter();
  const utmStore = useUtmStore();
  const authStore = useAuthStore();
  const trackingStore = useTrackingStore();

  function enableCopyQueryParams({ include, exclude } = {}) {
    copyQueryParams.value = true;
    includeQueryParams.value = Array.isArray(include) ? include : [];
    excludeQueryParams.value = Array.isArray(exclude) ? exclude : [];
  }

  function filterQueryParams(query = {}) {
    if (!copyQueryParams.value) {
      return undefined;
    }

    return Object.fromEntries(
      Object.entries(query).filter(
        ([param]) =>
          (!includeQueryParamsToCheck.value?.length ||
            includeQueryParamsToCheck.value.includes(param)) &&
          !(excludeQueryParamsToCheck.value ?? []).includes(param),
      ),
    );
  }

  async function performBrandSwitch(brandLabel, query) {
    isBrandSwitching.value = true;

    try {
      const result = await router.push({
        name: route.name,
        params: { brandLabel },
        query,
      });

      if (!isNavigationFailure(result)) {
        authStore.setCurrentBrand({ brandLabel });
      }

      return result;
    } finally {
      isBrandSwitching.value = false;
    }
  }

  async function switchBrand(brandLabel) {
    if (route?.params?.brandLabel) {
      const query = filterQueryParams(route.query);

      if (utmStore.isManagementSettingsDirty) {
        utmStore.confirmLeaving(async () => {
          await performBrandSwitch(brandLabel, query);
        });
      } else {
        await performBrandSwitch(brandLabel, query);
      }
    } else {
      authStore.setCurrentBrand({ brandLabel });
      nextTick(() => {
        // faking a Page Viewed event to keep metrics consistent
        trackingStore.trackPageViewedEvent(route, null);
      });
    }
  }

  tryOnUnmounted(() => {
    copyQueryParams.value = false;
    includeQueryParams.value = [];
    excludeQueryParams.value = [];
  });

  return {
    isBrandSwitching: readonly(isBrandSwitching),
    enableCopyQueryParams,
    filterQueryParams,
    switchBrand,
  };
}
